import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {Col, Row} from "reactstrap";
import {DropdownForm} from "../../base/dropdown/dropdown";
import {DatePickerForm} from "../../base/date-picker";
import Button from "../../base/button/button";
import Form from "../../base/form";
import useIsMounted from "../../../hooks/use-is-mounted";
import {InputForm} from "../../base/input/input";
import Api from "../../../../core/services/api/api";
import {Close} from "@mui/icons-material";
import DateUtils from "../../../../core/services/date-utils";
import {MuiRFF, Yup} from '../../form';
import MultiCustomerSearchSectionAutocomplete from "../../text-fields/autocomplete/customer-autocomplete/multi-search-section";


const formValues = {
    reportName: 'reportName',
    regions: 'regions',
    freightCompanies: 'freightCompanies',
    startDate: 'startDate',
    endDate: 'endDate',
}

const schema = Yup.object().shape({
    [formValues.reportName]: Yup.string().trim().nullable().required(ValidateMessages.required),
    [formValues.regions]: Yup.object().nullable(),
    [formValues.freightCompanies]: Yup.object().nullable(),
    [formValues.startDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .maxDate(Yup.ref(formValues.endDate), ValidateMessages.lessThan('End Date'))
        .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
        .nullable().required(ValidateMessages.required),
    [formValues.endDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .minDate(Yup.ref(formValues.startDate), ValidateMessages.greaterThan('Start Date'))
        .disableFutureDate(ValidateMessages.maxDate("today"), 'day')
        .nullable(),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const FreightFeesReportSearchSection = ({
                                            loading,
                                            handleAddingReport,
                                        }) => {
    const [companies, setCompanies] = useState([]);
    const [regions, setRegions] = useState([]);
    const [customers, setCustomers] = useState([]);
    const isMounted = useIsMounted();
    /**@type {React.MutableRefObject<formApi>}*/
    const formRef = useRef();
    const valueRef = useRef();

    /**
     * Calls the api for getting the list of companies from server
     * @type {(function(): Promise<boolean>)}
     */
    const getFreightCompanies = useCallback(async () => {
        const response = await Api.getFreightCompanies({});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setCompanies(response.data ?? []);
        }
    }, [isMounted])

    /**
     * Calls the api for getting the list of regions from server
     * @type {(function(): Promise<boolean>)}
     */
    const getFreightRegions = useCallback(async () => {
        const response = await Api.getSystemRegions({});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setRegions(response.data ?? []);
        }
    }, [isMounted])

    /**
     * With initial render of the component, Calls the function for getting the list of all companies, and reasons.
     */
    useEffect(() => {
        getFreightCompanies().then();
        getFreightRegions().then();
    }, [getFreightCompanies, getFreightRegions])

    /**
     * Submits the changes in the search section by changing the query parameters in the url
     * @type {(function(*, formApi): Promise<boolean>)}
     */
    const onSubmit = useCallback(async (values, form) => {
        const res = await handleAddingReport({
            reportName: values[formValues.reportName],
            regions: values[formValues.regions],
            customers: customers?.map(e => e.id),
            freightCompanies: values[formValues.freightCompanies],
            startDate: values.startDate ? DateUtils.UTCToServer(values.startDate).format(DateUtils.withoutOffsetFormat) : undefined,
            endDate: values.endDate ? DateUtils.UTCToServer(values.endDate).format(DateUtils.withoutOffsetFormat) : undefined,
        });
        if (!isMounted())
            return;
        if (!!res)
            return res;
        setTimeout(() => {
            if (!isMounted())
                return;
            form.change(formValues.reportName, null);
            form.change(formValues.regions, null);
            form.change(formValues.freightCompanies, null);
            form.change(formValues.startDate, DateUtils.localNowToUTC().subtract(30, 'day'));
            form.change(formValues.endDate, null);
            setCustomers([]);
            form.resetFieldState(formValues.reportName);
            form.resetFieldState(formValues.regions);
            form.resetFieldState(formValues.freightCompanies);
            form.resetFieldState(formValues.startDate);
            form.resetFieldState(formValues.endDate);
        }, 300);
        return res;
    }, [customers, handleAddingReport, isMounted])

    /**
     * Handles removing the selected entity from the list when remove icon button clicked on input chip.
     * @type {(function(*, *, *): void)}
     */
    const _removeSelectedEntity = useCallback((event, entity, formName) => {
        event.stopPropagation();
        event.preventDefault();
        const isLastItem = (valueRef.current?.category?.length ?? 0) < 2;
        formRef.current.change(formName, isLastItem
            ? undefined
            : valueRef.current?.[formName]?.filter(e => e.id !== entity.id)
        );
    }, [])

    /**
     * Renders the select options for the multi-select drop-downs.
     * @type {(function(*, number, number, string): *)}
     */
    const renderSelectedEntityValue = useCallback((item, index, length, formName) => {
        return index < 2
            ? <Button key={item.id ?? item.label ?? item.name ?? item.title}
                      onClick={(e) => _removeSelectedEntity(e, item, formName)}
                      className={'multi-select-input-chips'}>
                <div className={'remove-icon-container'}>
                    <Close className='icon extraSmall'/>
                </div>
                <p>
                    {item.label ?? item.name ?? item.title ?? ''}
                </p>
            </Button>
            : index === (length - 1)
                ? <div key={item.id ?? item.label ?? item.name ?? item.title} style={{height: '26px'}}
                       className={'d-flex align-items-center justify-content-center'}>
                    <p className={'text-sm font-weight-regular'}>
                        {`+${length - 2} more`}
                    </p>
                </div>
                : <></>
    }, [_removeSelectedEntity])

    const initialValues = useMemo(() => ({
        [formValues.startDate]: DateUtils.localNowToUTC().subtract(30, 'day'),
    }), []);


    return (
        <Form
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            className={'d-flex w-100 align-items-center justify-content-center'}
            render={({values, form}) => {
                formRef.current = form;
                valueRef.current = values;
                return (
                    <Row className='d-flex w-100 align-items-center justify-content-center'>
                        <Col xs={12} className={'mb-3'}>
                            <MultiCustomerSearchSectionAutocomplete
                                customers={customers}
                                setCustomers={setCustomers}
                            />
                        </Col>
                        <Col xs={12} className={'mb-3'}>
                            <InputForm
                                label={'Report Name'}
                                name={formValues.reportName}
                                type={'text'}
                                required={required[formValues.reportName]}
                            />
                        </Col>
                        <Col xs={12} md={6} className={'mb-3'}>
                            <DropdownForm
                                renderMultipleSelectValue={(...args) => renderSelectedEntityValue(...args, formValues.regions)}
                                padding={'3px 15px'}
                                label={'Regions'}
                                className={'py-0'}
                                name={formValues.regions}
                                required={false}
                                multiple
                                data={regions.map(e => ({
                                    value: e,
                                    label: e.title,
                                    id: e.id,
                                })) ?? []}
                            />
                        </Col>
                        <Col xs={12} md={6} className={'mb-3'}>
                            <DropdownForm
                                renderMultipleSelectValue={(...args) => renderSelectedEntityValue(...args, formValues.freightCompanies)}
                                padding={'3px 15px'}
                                label={'Freight Companies'}
                                className={'py-0'}
                                name={formValues.freightCompanies}
                                required={false}
                                multiple
                                data={companies.map(e => ({
                                    value: e,
                                    label: e.title,
                                    id: e.id,
                                })) ?? []}
                            />
                        </Col>
                        <Col xs={12} md={6} className={'mb-3'}>
                            <DatePickerForm
                                name={formValues.startDate}
                                label={'Start Date'}
                                required={required[formValues.startDate]}
                                maxDate={values[formValues.endDate]}
                                disableFuture
                            />
                        </Col>
                        <Col xs={12} md={6} className={'mb-3'}>
                            <DatePickerForm
                                name={formValues.endDate}
                                label={'End Date'}
                                required={required[formValues.endDate]}
                                minDate={values[formValues.startDate]}
                                disableFuture
                            />
                        </Col>
                        <Col xs={12} className='px-3 mt-2 align-items-center row no-gutters justify-content-center align-items-center'>
                            <Button
                                disabled={loading}
                                type={'submit'}
                                variant={'contained'}
                                color={'primary'}
                                className='py-3 px-8'
                            >
                                {
                                    loading
                                        ? "Adding Report..."
                                        : "Add Report"
                                }
                            </Button>
                        </Col>
                    </Row>
                );
            }}
        />
    );
}

export default FreightFeesReportSearchSection;
